import { CUSTOMER_GCS_BASE, isProductionDashboardHost } from "./productionOfferingRegistry";

const RELEASES = Object.freeze({
  NBA: {
    dynamic: true,
  },
  NFL: {
    dynamic: true,
  },
  WNBA: {
    dynamic: true,
  },
  CFB: {
    dynamic: true,
  },
  KBO: {
    dynamic: true,
  },
  SOCCER: {
    dynamic: true,
  },
  NHL: {
    dynamic: true,
  },
});

const immutableReleaseCache = new Map();

const cleanSport = (sport) => String(sport || "").trim().toUpperCase();
const projectionId = (row) => String(
  row?.["Projection ID"] ?? row?.ProjectionID ?? row?.projection_id ?? ""
).trim();

export const productionStaticCustomerSportEnabled = (sport) => {
  const normalized = cleanSport(sport);
  if (!RELEASES[normalized] || typeof window === "undefined") return false;
  return isProductionDashboardHost(window.location);
};

const urlFor = (sport, path) =>
  `${CUSTOMER_GCS_BASE}/${sport.toLowerCase()}/${String(path).replace(/^\/+/, "")}`;

async function fetchJson(url, mutable = false) {
  const response = await fetch(url, {
    cache: mutable ? "no-store" : "force-cache",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Static customer sports data unavailable (${response.status})`);
  return response.json();
}

const mutablePointerUrl = (sport) => {
  const url = urlFor(sport, "current.json");
  return RELEASES[cleanSport(sport)]?.dynamic === true ? `${url}?refresh=${Date.now()}` : url;
};

const nflGamesFromRows = (rows) => {
  const byId = new Map();
  rows.forEach((row) => {
    const gameId = String(row?.["Game ID"] || "").trim();
    if (!gameId) return;
    const current = byId.get(gameId) || { game_id: gameId, rows: 0 };
    current.rows += 1;
    current.matchup = current.matchup || String(row?.Matchup || row?.matchup || "").trim();
    current.away_team = current.away_team || String(row?.["Away Team"] || row?.away_team || "").trim();
    current.home_team = current.home_team || String(row?.["Home Team"] || row?.home_team || "").trim();
    byId.set(gameId, current);
  });
  return [...byId.values()];
};

async function fetchPayload(url) {
  const response = await fetch(url, {
    cache: "force-cache",
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Static customer sports payload unavailable (${response.status})`);
  const bytes = await response.arrayBuffer();
  if (!globalThis.crypto?.subtle) throw new Error("Static customer checksum validation is unavailable");
  const hash = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  const checksum = [...new Uint8Array(hash)]
    .map((value) => value.toString(16).padStart(2, "0")).join("");
  let payload;
  try { payload = JSON.parse(new TextDecoder().decode(bytes)); }
  catch { throw new Error("Static customer sports payload is malformed"); }
  return { payload, checksum, bytes: bytes.byteLength };
}

export function validateStaticCustomerRelease(sport, pointer, manifest, payloadResult) {
  const normalized = cleanSport(sport);
  const expected = RELEASES[normalized];
  if (!expected) throw new Error("Unsupported static customer sport");
  const lower = normalized.toLowerCase();
  const dynamicRelease = expected.dynamic === true;
  const version = dynamicRelease ? String(pointer?.version_id || "") : expected.version;
  const sha256 = dynamicRelease ? String(pointer?.source_sha256 || "") : expected.sha256;
  const expectedCounts = dynamicRelease
    ? [Number(pointer?.row_count), Number(pointer?.projection_id_count), Number(pointer?.game_count)]
    : [expected.rows, expected.projections, expected.games];
  const releasePath = `${lower}/releases/${version}`;
  if (
    pointer?.contract_version !== `PWG_${normalized}_POINTER_1.0` ||
    pointer?.sport !== normalized || pointer?.version_id !== version ||
    pointer?.release_path !== releasePath || pointer?.source_sha256 !== sha256 ||
    (dynamicRelease && (!/^sha256-[a-f0-9]{24}-v\d+$/.test(version) || !/^[a-f0-9]{64}$/.test(sha256) || expectedCounts.some((value) => !Number.isInteger(value) || value < 1)))
  ) throw new Error(`${normalized} customer pointer contract mismatch`);
  if (
    manifest?.contract_version !== `PWG_${normalized}_STATIC_1.0` ||
    manifest?.classification || manifest?.sport !== normalized ||
    manifest?.version_id !== version || manifest?.source_sha256 !== sha256
  ) throw new Error(`${normalized} customer manifest contract mismatch`);
  const descriptor = manifest?.artifact;
  if (
    descriptor?.path !== `${lower}-props.json` || descriptor?.sha256 !== sha256 ||
    Number(descriptor?.bytes) !== Number(payloadResult?.bytes) ||
    payloadResult?.checksum !== sha256
  ) throw new Error(`${normalized} customer payload checksum or path mismatch`);
  const rows = Array.isArray(payloadResult?.payload)
    ? payloadResult.payload
    : Array.isArray(payloadResult?.payload?.props) ? payloadResult.payload.props : [];
  const ids = rows.map(projectionId);
  const games = new Set(rows.map((row) => String(row?.["Game ID"] || "").trim()));
  const counts = [rows.length, new Set(ids).size, games.size];
  if (
    ids.some((id) => !id) || games.has("") ||
    counts.some((value, index) => value !== expectedCounts[index]) ||
    [manifest.row_count, manifest.projection_id_count, manifest.game_count]
      .some((value, index) => Number(value) !== expectedCounts[index]) ||
    [pointer.row_count, pointer.projection_id_count, pointer.game_count]
      .some((value, index) => Number(value) !== expectedCounts[index])
  ) throw new Error(`${normalized} customer payload accounting mismatch`);
  return rows;
}

export async function loadStaticCustomerSport(sport) {
  const normalized = cleanSport(sport);
  const expected = RELEASES[normalized];
  if (!expected) throw new Error("Unsupported static customer sport");
  const lower = normalized.toLowerCase();
  const pointer = await fetchJson(mutablePointerUrl(normalized), true);
  const dynamicRelease = expected.dynamic === true;
  const version = dynamicRelease ? String(pointer?.version_id || "") : expected.version;
  const expectedPath = `${lower}/releases/${version}`;
  if (pointer?.release_path !== expectedPath) {
    throw new Error(`${normalized} customer pointer references an unexpected release`);
  }
  const releaseRelative = `releases/${version}`;
  const loadImmutable = () => Promise.all([
      fetchJson(urlFor(normalized, `${releaseRelative}/manifest.json`)),
      fetchPayload(urlFor(normalized, `${releaseRelative}/${lower}-props.json`)),
    ]);
  let immutablePromise;
  if (dynamicRelease) {
    immutablePromise = immutableReleaseCache.get(expectedPath);
    if (!immutablePromise) {
      immutablePromise = loadImmutable();
      immutableReleaseCache.set(expectedPath, immutablePromise);
      immutablePromise.catch(() => immutableReleaseCache.delete(expectedPath));
    }
  } else {
    immutablePromise = loadImmutable();
  }
  const [manifest, payloadResult] = await immutablePromise;
  const rows = validateStaticCustomerRelease(normalized, pointer, manifest, payloadResult);
  return {
    props: rows,
    injuryReport: Array.isArray(payloadResult.payload?.injury_report)
      ? payloadResult.payload.injury_report : [],
    games: dynamicRelease ? nflGamesFromRows(rows) : [],
    version: {
      version_id: pointer.version_id,
      source_sha256: pointer.source_sha256,
      row_count: rows.length,
      game_count: Number(pointer.game_count),
    },
  };
}

export const __STATIC_CUSTOMER_RELEASES_FOR_TESTS = RELEASES;
export const __resetStaticCustomerReleaseCacheForTests = () => immutableReleaseCache.clear();
